import * as Sentry from "@sentry/react";
import type { Event } from "@sentry/react";

/** Form copy when the form opens because something crashed, not from the nav drawer. */
const CRASH_FORM_TEXT = {
	formTitle: "Something went wrong",
	messagePlaceholder: "What were you doing when this happened?",
	submitButtonLabel: "Send report",
};

// The widget's form has no `associatedEventId` option (only the `captureFeedback` API takes one),
// and its `tags` option would stick to the scope and land on every later event. So the crash's
// event id is held here while its form is open, and `linkFeedbackToCrash` writes it into the
// feedback event, which is what makes Sentry show the report on the crash's issue.
let crashEventId: string | undefined;
let formOpen = false;
let linkerInstalled = false;

function linkFeedbackToCrash(event: Event): Event {
	const feedback = event.contexts?.feedback;
	if (event.type === "feedback" && crashEventId && feedback && !feedback.associated_event_id) {
		feedback.associated_event_id = crashEventId;
	}
	return event;
}

async function openForm(crash?: { eventId: string }) {
	const feedback = Sentry.getFeedback();
	// One form at a time: a burst of errors must not stack a dialog per error.
	if (!feedback || formOpen) return;
	formOpen = true;
	crashEventId = crash?.eventId;
	if (crash && !linkerInstalled) {
		Sentry.getGlobalScope().addEventProcessor(linkFeedbackToCrash);
		linkerInstalled = true;
	}
	try {
		const form = await feedback.createForm({
			...(crash && CRASH_FORM_TEXT),
			onFormClose: () => close(form),
			onFormSubmitted: () => close(form),
		});
		form.appendToDom();
		form.open();
	} catch (error) {
		close();
		throw error;
	}
}

function close(form?: { removeFromDom(): void }) {
	formOpen = false;
	crashEventId = undefined;
	form?.removeFromDom();
}

/**
 * Browser implementation of `IFeedback` that opens Sentry's hosted
 * feedback form. Silently no-ops when Sentry's feedback integration
 * isn't initialised (e.g. when SDK init failed at boot).
 */
export const sentryFeedback = {
	openForm: () => openForm(),
	/** Opens the form as a crash report, linked to the error event `eventId`. */
	openCrashForm: (eventId: string) => openForm({ eventId }),
};
